import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { TicketList } from '../components/tickets/TicketList';
import { TicketFilters } from '../components/tickets/TicketFilters';
import { TicketForm } from '../components/tickets/TicketForm';
import { PageHeader, Button, Modal, LoadingSpinner, EmptyState } from '../components/ui';
import { ticketService } from '../services/ticket.service';
import { messageHandler } from '../services/message-handler.service';
import type { Ticket, TicketFormData, TicketFilters as TicketFiltersType } from '../types/ticket';

function Tickets() {
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<TicketFiltersType>({});
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);

  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['tickets', currentPage, filters],
    queryFn: () => ticketService.list(currentPage, 20, filters),
  });

  const createMutation = useMutation({
    mutationFn: ticketService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      setIsCreateModalOpen(false);
      messageHandler.showSuccess('Ticket criado com sucesso');
    },
    onError: () => {
      messageHandler.showError('Erro ao criar ticket');
    },
  });

  const handleCreate = (data: TicketFormData) => {
    createMutation.mutate(data);
  };

  const handleView = (ticket: Ticket) => {
    setSelectedTicket(ticket);
    setIsViewModalOpen(true);
  };

  const handleFilter = (newFilters: TicketFiltersType) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="text-red-600">Erro ao carregar tickets</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <PageHeader
        title="Tickets"
        description="Gerencie os tickets de atendimento"
        action={
          <Button onClick={() => setIsCreateModalOpen(true)}>
            Novo Ticket
          </Button>
        }
      />

      <TicketFilters
        onFilter={handleFilter}
        initialFilters={filters}
      />

      {data?.data && data.data.length > 0 ? (
        <TicketList
          tickets={data.data}
          pagination={data.pagination}
          onPageChange={handlePageChange}
          onView={handleView}
        />
      ) : (
        <EmptyState
          title="Nenhum ticket encontrado"
          description="Crie seu primeiro ticket para começar"
        />
      )}

      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Novo Ticket"
      >
        <TicketForm
          onSubmit={handleCreate}
          onCancel={() => setIsCreateModalOpen(false)}
        />
      </Modal>

      <Modal
        isOpen={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
          setSelectedTicket(null);
        }}
        title="Detalhes do Ticket"
      >
        {selectedTicket && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ID
              </label>
              <p className="text-gray-900">#{selectedTicket.id}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Título
              </label>
              <p className="text-gray-900">{selectedTicket.title}</p>
            </div>

            {selectedTicket.description && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição
                </label>
                <p className="text-gray-900">{selectedTicket.description}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <p className="text-gray-900">
                  {selectedTicket.status === 'open' && 'Aberto'}
                  {selectedTicket.status === 'pending' && 'Pendente'}
                  {selectedTicket.status === 'closed' && 'Fechado'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sessão
                </label>
                <p className="text-gray-900">{selectedTicket.session_id}</p>
              </div>
            </div>

            {selectedTicket.contact_name && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contato
                </label>
                <p className="text-gray-900">{selectedTicket.contact_name}</p>
              </div>
            )}

            {selectedTicket.assistant_name && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assistente
                </label>
                <p className="text-gray-900">{selectedTicket.assistant_name}</p>
              </div>
            )}

            {selectedTicket.user_account_name && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Atendente
                </label>
                <p className="text-gray-900">{selectedTicket.user_account_name}</p>
              </div>
            )}

            {selectedTicket.queue_name && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fila
                </label>
                <p className="text-gray-900">{selectedTicket.queue_name}</p>
              </div>
            )}

            {selectedTicket.last_message && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Última Mensagem
                </label>
                <p className="text-gray-900">{selectedTicket.last_message}</p>
              </div>
            )}

            {selectedTicket.last_resume && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Último Resumo
                </label>
                <p className="text-gray-900">{selectedTicket.last_resume}</p>
              </div>
            )}

            <div className="flex justify-end pt-4">
              <Button
                variant="secondary"
                onClick={() => {
                  setIsViewModalOpen(false);
                  setSelectedTicket(null);
                }}
              >
                Fechar
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default Tickets;
